import { http } from '@/utils/HttpClient';
import endpoints from '@/endpoints';
import { ApiResponse } from '@/types/api';
import { auth } from '@/lib/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';

export type PaymentProduct = {
  id: string;
  productId: string;
  description: string;
  type: string;
  name: string;
  billingCycle: {
    interval: 'day' | 'week' | 'month' | 'year';
    frequency: number;
  };
  trialPeriod?: {
    interval: 'day' | 'week' | 'month' | 'year';
    frequency: number;
  } | null;
  trialPeriodUnitPrice?: {
    amount: string;
    currencyCode: string;
  } | null;
  unitPrice: {
    amount: string;
    currencyCode: string;
  };
  quantity?: {
    minimum: number;
    maximum: number;
  };
  status: string;
  customData?: {
    trial?: boolean;
    discountId?: string;
    discountPercentage?: string;
    minQuantity?: number;
    maxQuantity?: number;
    priceType?: string;
    productType?: string;
    title?: string;
  };
};

export type PaymentTransactionStatus =
  | 'draft'
  | 'ready'
  | 'billed'
  | 'paid'
  | 'completed'
  | 'canceled'
  | 'past_due';

export type PaymentTransaction = {
  id: string;
  status: PaymentTransactionStatus;
  currency_code: string;
  invoiceNumber?: string | null;
  invoice_number?: string | null;
  billedAt?: string | null;
  billed_at?: string | null;
  details: {
    totals: {
      total: string;
      currencyCode?: string;
      currency_code?: string;
    };
  };
  payments: Array<{
    status: string;
    error_code?: string | null;
  }>;
};

export type PaymentSubscription = any;

const getFirebaseIdToken = async (): Promise<string | null> => {
  if (auth.currentUser) {
    return await auth.currentUser.getIdToken().catch(() => null);
  }

  return await new Promise((resolve) => {
    let resolved = false;
    const timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        unsubscribe();
        resolve(null);
      }
    }, 1500);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timeoutId);
      unsubscribe();
      if (!user) {
        resolve(null);
        return;
      }
      const token = await user.getIdToken().catch(() => null);
      resolve(token);
    });
  });
};

export const paymentService = {
  async getProducts(): Promise<ApiResponse<PaymentProduct[]>> {
    const productId = process.env.NEXT_PUBLIC_PADDLE_PRODUCT_ID;
    if (!productId) {
      throw new Error('NEXT_PUBLIC_PADDLE_PRODUCT_ID is not set');
    }
    const baseURL = process.env.NEXT_PUBLIC_PAYMENT_API_URL;
    if (!baseURL) {
      throw new Error('NEXT_PUBLIC_PAYMENT_API_URL is not set');
    }
    const token = await getFirebaseIdToken();

    return await http.fetch({
      method: endpoints.payment.product.get.method,
      endpoint: endpoints.payment.product.get.url(),
      config: {
        baseURL,
        skipAuth: true,
        headers: {
          'x-platform': 'web',
          ...(token ? { 'x-token': token } : {}),
        },
        body: {
          productId,
        },
      },
    });
  },

  async createTransaction(payload: {
    priceId: string;
    trackingId: string | null;
    hubxId: string | null;
    paywallType: string;
    discountId?: string;
    quantity?: number;
    toolType?: string;
  }): Promise<ApiResponse<{ id: string }>> {
    const baseURL = process.env.NEXT_PUBLIC_PAYMENT_API_URL;
    if (!baseURL) {
      throw new Error('NEXT_PUBLIC_PAYMENT_API_URL is not set');
    }
    const token = await getFirebaseIdToken();

    const response = await http.fetch<{
      success?: boolean;
      data?: { id: string };
      time?: string;
      id?: string;
    }>({
      method: endpoints.payment.transaction.create.method,
      endpoint: endpoints.payment.transaction.create.url(),
      config: {
        baseURL,
        skipAuth: true,
        headers: {
          'x-platform': 'web',
          ...(token ? { 'x-token': token } : {}),
        },
        body: payload,
      },
    });

    const normalized =
      (response.data as any)?.data ?? (response.data as any) ?? null;

    return {
      ...response,
      data: normalized as { id: string },
    };
  },

  async getTransactions(payload: {
    statusList: PaymentTransactionStatus[];
  }): Promise<ApiResponse<PaymentTransaction[]>> {
    const baseURL = process.env.NEXT_PUBLIC_PAYMENT_API_URL;
    if (!baseURL) {
      throw new Error('NEXT_PUBLIC_PAYMENT_API_URL is not set');
    }
    const token = await getFirebaseIdToken();

    const response = await http.fetch<{
      success?: boolean;
      data?: PaymentTransaction[];
      time?: string;
    }>({
      method: endpoints.payment.transaction.list.method,
      endpoint: endpoints.payment.transaction.list.url(),
      config: {
        baseURL,
        skipAuth: true,
        headers: {
          'x-platform': 'web',
          ...(token ? { 'x-token': token } : {}),
        },
        body: payload,
      },
    });

    const normalized =
      (response.data as any)?.data ?? (response.data as any) ?? [];

    return {
      ...response,
      data: normalized as PaymentTransaction[],
    };
  },

  async getInvoice(payload: {
    transactionId: string;
  }): Promise<ApiResponse<string>> {
    const baseURL = process.env.NEXT_PUBLIC_PAYMENT_API_URL;
    if (!baseURL) {
      throw new Error('NEXT_PUBLIC_PAYMENT_API_URL is not set');
    }
    const token = await getFirebaseIdToken();

    const response = await http.fetch<{
      success?: boolean;
      data?: string;
      time?: string;
    }>({
      method: endpoints.payment.transaction.invoice.method,
      endpoint: endpoints.payment.transaction.invoice.url(),
      config: {
        baseURL,
        skipAuth: true,
        headers: {
          'x-platform': 'web',
          ...(token ? { 'x-token': token } : {}),
        },
        body: payload,
      },
    });

    const normalized =
      (response.data as any)?.data ?? (response.data as any) ?? null;

    return {
      ...response,
      data: normalized as string,
    };
  },

  async getSubscription(): Promise<ApiResponse<PaymentSubscription>> {
    const baseURL = process.env.NEXT_PUBLIC_PAYMENT_API_URL;
    if (!baseURL) {
      throw new Error('NEXT_PUBLIC_PAYMENT_API_URL is not set');
    }
    const token = await getFirebaseIdToken();

    const response = await http.fetch<{
      success?: boolean;
      data?: PaymentSubscription;
      time?: string;
    }>({
      method: endpoints.payment.subscription.get.method,
      endpoint: endpoints.payment.subscription.get.url(),
      config: {
        baseURL,
        skipAuth: true,
        headers: {
          'x-platform': 'web',
          ...(token ? { 'x-token': token } : {}),
        },
      },
    });

    const normalized =
      (response.data as any)?.data ?? (response.data as any) ?? null;

    return {
      ...response,
      data: normalized as PaymentSubscription,
    };
  },

  async cancelSubscription(): Promise<ApiResponse<PaymentSubscription>> {
    const baseURL = process.env.NEXT_PUBLIC_PAYMENT_API_URL;
    if (!baseURL) {
      throw new Error('NEXT_PUBLIC_PAYMENT_API_URL is not set');
    }
    const token = await getFirebaseIdToken();

    const response = await http.fetch<{
      success?: boolean;
      data?: PaymentSubscription;
      time?: string;
    }>({
      method: endpoints.payment.subscription.cancel.method,
      endpoint: endpoints.payment.subscription.cancel.url(),
      config: {
        baseURL,
        skipAuth: true,
        headers: {
          'x-platform': 'web',
          ...(token ? { 'x-token': token } : {}),
        },
      },
    });

    const normalized =
      (response.data as any)?.data ?? (response.data as any) ?? null;

    return {
      ...response,
      data: normalized as PaymentSubscription,
    };
  },

  async updateSubscription(): Promise<ApiResponse<PaymentSubscription>> {
    const baseURL = process.env.NEXT_PUBLIC_PAYMENT_API_URL;
    if (!baseURL) {
      throw new Error('NEXT_PUBLIC_PAYMENT_API_URL is not set');
    }
    const token = await getFirebaseIdToken();

    const response = await http.fetch<{
      success?: boolean;
      data?: PaymentSubscription;
      time?: string;
    }>({
      method: endpoints.payment.subscription.update.method,
      endpoint: endpoints.payment.subscription.update.url(),
      config: {
        baseURL,
        skipAuth: true,
        headers: {
          'x-platform': 'web',
          ...(token ? { 'x-token': token } : {}),
        },
      },
    });

    const normalized =
      (response.data as any)?.data ?? (response.data as any) ?? null;

    return {
      ...response,
      data: normalized as PaymentSubscription,
    };
  },

  async upgradeSubscription(payload: {
    priceId: string;
    paywallType: string;
  }): Promise<ApiResponse<PaymentSubscription>> {
    const baseURL = process.env.NEXT_PUBLIC_PAYMENT_API_URL;
    if (!baseURL) {
      throw new Error('NEXT_PUBLIC_PAYMENT_API_URL is not set');
    }
    const token = await getFirebaseIdToken();

    try {
      const response = await http.fetch<{
        success?: boolean;
        data?: PaymentSubscription;
        time?: string;
      }>({
        method: endpoints.payment.subscription.upgrade.method,
        endpoint: endpoints.payment.subscription.upgrade.url(),
        config: {
          baseURL,
          skipAuth: true,
          headers: {
            'x-platform': 'web',
            ...(token ? { 'x-token': token } : {}),
          },
          body: payload,
        },
      });

      const normalized =
        (response.data as any)?.data ?? (response.data as any) ?? null;

      return {
        ...response,
        data: normalized as PaymentSubscription,
      };
    } catch (error: any) {
      // Fallback to /api/subscription/update if upgrade endpoint is missing.
      if (error?.status !== 404) {
        throw error;
      }

      const response = await http.fetch<{
        success?: boolean;
        data?: PaymentSubscription;
        time?: string;
      }>({
        method: 'POST',
        endpoint: endpoints.payment.subscription.update.url(),
        config: {
          baseURL,
          skipAuth: true,
          headers: {
            'x-platform': 'web',
            ...(token ? { 'x-token': token } : {}),
          },
          body: payload,
        },
      });

      const normalized =
        (response.data as any)?.data ?? (response.data as any) ?? null;

      return {
        ...response,
        data: normalized as PaymentSubscription,
      };
    }
  },
};
